import React from 'react';
import Dropdown from './ui/Dropdown';
import { BTN, BTN_XS } from '../constants/buttons';

/**
 * OrderActionsMenu — the `Actions ▾` disclosure on an expanded order row.
 *
 * Product owner, 2026-08-10: *"lets look at the action menu. Pelase repace
 * "View Details / Edit / Approve / Reject / Download / Print / Status Override:
 * Submitted (current)" actions with the single actions menu. We will also
 * include an export excell and print options in the action menu please"*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THIS COMPONENT RESOLVES NO PERMISSIONS AND DECIDES NOTHING.
 * ═══════════════════════════════════════════════════════════════════════════
 * It receives a FINISHED, ALREADY-GATED array of items and renders it. That is
 * deliberately the same contract `components/OrderTableView.js` has, and for
 * the same reason recorded in CLAUDE.md's sanctioned-exception box: the moment
 * a shared presentation component can decide who sees an action, the two pages
 * that use it can disagree about who may do what, and neither page's gate is
 * the answer any more.
 *
 * 🔑 **EACH PAGE BUILDS ITS OWN ITEM ARRAY, WITH EVERY GATE WRITTEN VERBATIM
 * BESIDE ITS ITEM, IN THE PLACE THAT GATE ALREADY LIVED.** `DepartmentOrders`
 * carries the full status lifecycle (Mark Ordered / Partial Delivery / Mark
 * Delivered / Restore) plus the status override; `Approvals` carries Approve +
 * Reject and no lifecycle at all. What the two pages share is the CHROME and
 * the placement rule — never the items and never the gates. Do not "unify" the
 * arrays; the 2026-08-09 both-branches defect is what that produces.
 *
 * 🪤 **`portal` IS ON, AND IT IS NOT OPTIONAL HERE.** The trigger sits inside
 * the expanded panel, which is inside `OrderTableShell`'s clipping rows pane
 * (height-capped `overflow: auto` until 2026-09-26; `overflow-x: auto` +
 * `overflow-y: hidden` since) and, in the table layout, inside a `<td>`. An
 * absolutely-positioned panel cannot escape an ancestor's `overflow` clip at
 * ANY z-index — that is the measured lesson of the 2026-08-09 Scenes-picker
 * defect recorded in `ui/Dropdown`'s docblock, where the panel overflowed its
 * cell by 250px and 0 of 5 probes hit it. Measured again for this menu on the
 * LAST row of both pages before it shipped. **`z-index` cannot un-clip.**
 *
 * 📱 Below `md` the trigger is full-width (`w-full`) rather than sharing the
 * 2-column grid: it is the widest label in the row and the only control that
 * opens something, and a half-width trigger next to an odd number of buttons
 * wraps badly. Items are `min-h-[44px]` single-column rows; `ui/Dropdown`'s
 * panel already carries `max-w-[calc(100vw-1rem)]` and a `max-h` with
 * `overflow-y-auto`, so a long menu scrolls instead of running off a phone.
 *
 * Item shapes — falsy entries are dropped, so a page can write
 * `someGate && { … }` inline and keep the gate beside the item:
 *
 *   { key, label, onClick, testId, title?, tone?, icon?, disabled? }
 *       an ordinary menu row.
 *
 *   { key, render: (close) => node }
 *       an escape hatch for a row that owns internal state and cannot be
 *       expressed as label+onClick — today the PDF actions, which need their
 *       own `pendingAction`. `close` is `ui/Dropdown`'s, so the row can dismiss
 *       the panel when its work is done.
 *
 * Props:
 *  - items: array (see above)
 *  - footer: node | (close) => node — rendered under a separator, after the
 *    items. For a CONTROL you set rather than an action you fire (the status
 *    override `<select>`), which must not look like the rows above it.
 *  - label: trigger text. Defaults to 'Actions'.
 *  - triggerTestId / panelTestId
 *  - triggerClassName: defaults to the site's secondary button.
 *  - compact: see below.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 `compact` (2026-08-11) — THE ROW-LEVEL FORM, FOR A MENU THAT SITS ON A
 *    LINE ITEM RATHER THAN ON AN ORDER.
 * ═══════════════════════════════════════════════════════════════════════════
 * Added for `components/OrderItemActionsMenu.js`. **Defaults `false`, so the
 * two existing callers (`DepartmentOrders.js`, `Approvals.js`) render a
 * byte-identical trigger** — that is what makes this additive rather than a
 * restyle, and it is the property to preserve if anything here is ever
 * retuned.
 *
 * It changes exactly three things and none is a colour:
 *  1. **The trigger stops being full-width below `md`.** The default
 *     `w-full md:w-auto` is right for the ORDER menu, which owns its own row
 *     in the expanded panel and is the widest label in it. A LINE-ITEM menu
 *     sits beside a description in a table cell or a card header, so a
 *     full-width trigger would push the item's own content onto another line
 *     on every row.
 *  2. **`BTN_XS.secondary` instead of `BTN.secondary`.** One menu per ORDER is
 *     a page-level control; one menu per LINE is up to six on a single
 *     expanded row, and at `BTN.secondary` they out-weigh the data they act
 *     on. This is the *ghost/secondary* end of the three-weights rule in
 *     `ui/CLAUDE.md`, not a new weight.
 *  3. **The PANEL narrows too, `w-64` → `w-56` (2026-08-16).** Owner report: the
 *     line menu "renders wider than its trigger" and reads disproportionate —
 *     it was carrying the ORDER menu's panel width unconditionally, so a
 *     `BTN_XS` trigger a few characters wide opened a 256px panel regardless
 *     of what a compact menu's own (shorter) items need. `w-56` is not a new
 *     number: it is the width `GagBreakdown.js`'s own compact `Actions ▾` and
 *     `MyTeamTasks.js`'s row-scale menu already use, so this brings
 *     `OrderItemActionsMenu` onto the app's existing row/compact-scale
 *     convention rather than inventing a fourth width. **`w-64` is UNCHANGED
 *     for `compact={false}`** — `DepartmentOrders.js` and `Approvals.js`
 *     render byte-identical panels; see `panelWidthClass` below.
 *
 * 🚨 **IT DOES NOT DROP `min-h-[44px] md:min-h-0`, AND MUST NEVER BE
 *    "FINISHED" BY DOING SO.** `BTN_XS` is a smaller box, so the temptation is
 *    to let the height follow it — but the 44px floor below `md` is the tap
 *    target (`constants/inputs.js`, `CONTROL_HEIGHT`), and a compact control
 *    is exactly where an unprefixed height "looks perfectly fine on a
 *    desktop" and drops every phone user under the minimum. The two are
 *    independent: `compact` is about WIDTH and WEIGHT, the floor is about
 *    TOUCH.
 *
 * 🪤 `portal` stays ON in both forms, and for a STRONGER reason in the compact
 *    one: a line-item menu is nested one level deeper — inside the item table
 *    inside the expanded panel inside `OrderTableShell`'s clipping rows
 *    pane — so it has strictly more clipping ancestors than
 *    the order menu that was measured at 0/5 probes without it.
 */

// Light + dark for every tone, per the house rule that a colour class without
// its `dark:` partner is a bug. `neutral` is the default because most items in
// this menu are neither destructive nor affirmative.
const TONE = {
  neutral: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700',
  approve: 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30',
  reject: 'text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30',
  progress: 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30',
  deliver: 'text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30',
  nav: 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
};

export const MENU_ITEM_BASE =
  'w-full flex items-center gap-2 text-left px-3 py-2 min-h-[44px] md:min-h-0 rounded-md ' +
  'text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * The complete neutral menu row — base + the `neutral` tone — for a menu that
 * builds its own `<button role="menuitem">` rows rather than going through this
 * component's `items` array.
 *
 * 🔑 **It exists so the PAGE-LEVEL `Actions ▾` in each order page's header band
 * cannot drift from the ROW-LEVEL one rendered here.** Both order pages needed
 * this string on 2026-08-10 (`Export CSV` /
 * `Reset column widths`); writing it out twice would have been the second and
 * third near-copies of a class already defined here, which is exactly what the
 * Code Unification Principle forbids.
 *
 * ⚠️ **`GagBreakdown.js`'s private `GAG_ACTIONS_MENU_ITEM` is a FOURTH near-copy
 * and is deliberately NOT migrated in this change** — it differs (no `flex`, no
 * `gap-2`, no 44px minimum, a `focus-visible` ring, and a
 * `disabled:hover:bg-transparent` pair) and folding it in would be an
 * unrequested visual change to a bar the owner is actively comparing. Flagged,
 * not done.
 */
export const MENU_ITEM_NEUTRAL = `${MENU_ITEM_BASE} ${TONE.neutral}`;

/**
 * The TYPOGRAPHY half of a category heading in this menu — extracted from the
 * heading `<div>` below (2026-09-13) so it has exactly one definition. It is
 * the label weight for anything that reads as a SECTION LABEL inside this
 * menu, including `InlineStatusJump`'s "Override order" — owner ruling
 * 2026-09-13: the override label must not out-weigh the group headings above
 * it, which is what made it look like the natural place to go. Pair it with
 * spacing (`px-3 pt-1.5 pb-1` here) at each call site; it carries no spacing
 * of its own because the footer that hosts "Override order" needs none of it.
 */
export const MENU_SECTION_HEADING_TEXT =
  'text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 SECTIONS (2026-08-11) — HEADED GROUPS IN ONE SCROLLING PANEL.
 * ═══════════════════════════════════════════════════════════════════════════
 * Opt in by passing `categories`; omit it and this renders the flat menu it
 * always rendered, byte for byte. `components/OrderItemActionsMenu.js` passes
 * none, so the line-item menu is untouched.
 *
 * Product owner's complaint was that the override *"doesn't fit"* — a space and
 * organisation problem. Sections solve exactly that.
 *
 * 🥇 **A CATEGORY RAIL (`ui/FiltersMasterDetail`) WAS BUILT FIRST AND REJECTED,
 * AND THE REASON IS WORTH MORE THAN THE CODE.** The Breakdown's rail works
 * because **filters are somewhere you DWELL** — you open the panel, tick things
 * across several categories, watch counts move. **Actions are the opposite: you
 * arrive knowing what you want, click once, and leave.** A rail charges a
 * navigation step for a single-verb action, and for ~10 items that is more
 * machinery than the problem needs. It also mounts only the ACTIVE category, so
 * an item in an unselected category is ABSENT FROM THE DOM — which took 28
 * client tests red across 7 suites and would have changed the contract of two
 * class ratchets. **Do not "upgrade" this to a rail.** The abandoned attempt is
 * `feat/order-actions-categorised-wip` (`df288d13`), marked DO NOT MERGE.
 *
 * 🔑 **NOTHING ELSE CHANGES, AND THAT IS THE POINT OF THIS VARIANT.** Every item
 * is in the DOM as soon as the panel opens and every action is one click, so:
 * the panel keeps `role="menu"`, the items keep `role="menuitem"`, no locator
 * moves, and the 2026-08-10 role incident (moving these controls into a menu
 * changed their computed role from `button` to `menuitem` and cost three red
 * staging runs) **cannot recur, because no role changes.**
 *
 * ♿ **A SECTION IS `role="group"` WITH `aria-labelledby` POINTING AT ITS
 * HEADING — NOT A BARE STYLED `<div>`.** `group` is a permitted child of `menu`
 * (a bare `<div>` with a visual heading conveys the grouping to sighted users
 * only), and its `menuitem` children are unaffected. `aria-labelledby` rather
 * than `aria-label` so the accessible name is the visible text, with no second
 * copy of the string to drift.
 *
 * 🪤 The heading is a plain `<div>`, deliberately: it must not be focusable and
 * must not be a `menuitem`. It carries `aria-hidden` NOWHERE — it is the group's
 * accessible name via the id reference, and hiding it would break that.
 *
 * Categories: `[{ key, label, footer? }]`. An item joins one via `category`.
 * 🔑 **An item with no `category`, or one naming a category not in the list,
 * falls into the FIRST category rather than vanishing** — losing an action
 * silently is a permission-shaped failure (the control is simply absent, exactly
 * as if a gate had refused it); showing it under the wrong heading is visible and
 * harmless by comparison.
 * ⚠️ **Empty categories are DROPPED** — items are already `someGate && {…}`, so
 * on a row the viewer cannot action, `Approval` has no items and no heading is
 * rendered for it. With only one category surviving, the heading is dropped too:
 * a single "Order" heading over the whole menu labels nothing.
 */
export default function OrderActionsMenu({
  items = [],
  footer = null,
  categories = null,
  label = 'Actions',
  triggerTestId,
  panelTestId,
  ariaLabel = 'Order actions',
  triggerClassName,
  compact = false,
  idPrefix = 'order-actions',
}) {
  const visible = (items || []).filter(Boolean);

  const catList = categories || [];
  const categoryOf = (item) => (
    catList.some((c) => c.key === item.category) ? item.category : (catList[0] && catList[0].key)
  );
  // 🚨 **`|| c.footer` IS LOAD-BEARING AND ITS ABSENCE WOULD HIDE A CONTROL.**
  // Department Orders' `Status` category holds the status-override `<select>` as
  // its footer, and its ITEMS are all status-conditional (Mark Ordered needs
  // `approved`, Partial Delivery needs `ordered`, …). On a `submitted` order
  // none of them passes, so an items-only test would drop the category and take
  // the override with it — a control that is live today disappearing on exactly
  // the orders whose status you are most likely to want to change. A category
  // key must never be able to hide anything; this is what enforces that.
  const liveCategories = catList
    .map((c) => ({ ...c, items: visible.filter((it) => categoryOf(it) === c.key) }))
    .filter((c) => c.items.length > 0 || c.footer);

  // One surviving category renders as a plain flat list — see the docblock.
  const sectioned = liveCategories.length > 1;

  // 🚨 `min-h-[44px] md:min-h-0` is in BOTH branches and is not part of what
  //    `compact` varies — see the docblock. Width and weight change; the tap
  //    target does not.
  const widthClasses = compact ? '' : 'w-full md:w-auto ';
  const justifyClasses = compact ? 'justify-center ' : 'justify-center md:justify-start ';
  const defaultTrigger = compact ? BTN_XS.secondary : BTN.secondary;
  // 🔀 PANEL WIDTH, 2026-08-16 — `compact` NOW ALSO NARROWS THE PANEL.
  // Owner: the per-line `Actions ▾` panel "renders wider than its trigger" and
  // reads as disproportionate. It is: the trigger is `BTN_XS.secondary` (a
  // handful of characters, ~13px tall text) while the panel was the SAME
  // fixed `w-64` (16rem/256px) as the ORDER-level menu's `BTN.secondary`
  // trigger, whose own label ("Actions ▾") is wider to start with. A menu MAY
  // legitimately be wider than its trigger — that is the nature of a
  // disclosure — so the fix is not "match the trigger", it is "stop using the
  // order-level width for a menu whose items are shorter". This component's
  // own longest COMPACT item is `OrderItemActionsMenu`'s "Edit notification…"
  // (19 characters); `w-56` (14rem/224px) — the same width `GagBreakdown.js`'s
  // own compact `Actions ▾` and `MyTeamTasks.js`'s row-scale menu already use
  // for near-identical content, so this is not a new number in the app, it is
  // the existing "row/compact-scale menu" convention this component had not
  // picked up. **`w-64` is UNCHANGED for the non-compact (order-level) menu**
  // — `DepartmentOrders.js` and `Approvals.js` render byte-identical panels.
  const panelWidthClass = compact ? 'w-56' : 'w-64';

  return (
    // `data-order-actions-menu` — how `OrderSectionHeader` finds the menu among
    // the header's buttons on a phone (it puts it on the title line).
    <div className={compact ? 'inline-block' : 'w-full md:w-auto'} data-order-actions-menu="">
      <Dropdown
        label={label}
        // 🪤 See the docblock — this is the clip escape hatch, not a z-index
        // tweak, and it must not be turned off without re-measuring at the
        // last row of the list.
        portal
        align="left"
        haspopup="menu"
        ariaLabel={ariaLabel}
        triggerTestId={triggerTestId}
        triggerClassName={`${widthClasses}${justifyClasses}min-h-[44px] md:min-h-0 ${triggerClassName || defaultTrigger}`}
        panelClassName={`${panelWidthClass} p-1`}
      >
        {({ close }) => {
          /**
           * ONE definition of a menu row, rendered by BOTH modes. 🔑 The flat
           * menu and every section must produce the SAME `role="menuitem"`
           * button — that identity is what keeps every existing locator working,
           * so it may not be written twice.
           */
          const renderItem = (item) => (
            typeof item.render === 'function' ? (
              <React.Fragment key={item.key}>{item.render(close)}</React.Fragment>
            ) : (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                // 🔑 `close()` AFTER the handler, never before: the handlers
                // these items call are the page's own `runRowAction` /
                // `saveThenRun`, which may read focus or pulse a chip, and a
                // panel that unmounted first would take the focus target with
                // it. `stopPropagation` matches every other control in the
                // expanded panel — the row itself is a click target.
                onClick={(e) => { e.stopPropagation(); item.onClick(e); close(); }}
                disabled={item.disabled}
                data-testid={item.testId}
                title={item.title}
                className={`${MENU_ITEM_BASE} ${TONE[item.tone] || TONE.neutral}`}
              >
                {item.icon}
                {item.label}
              </button>
            )
          );

          const renderFooter = (node) => node && (
            <>
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <div className="px-3 py-2">
                {typeof node === 'function' ? node(close) : node}
              </div>
            </>
          );

          return (
            <div role="menu" data-testid={panelTestId}>
              {sectioned ? liveCategories.map((cat, i) => (
                <div
                  key={cat.key}
                  role="group"
                  aria-labelledby={`${idPrefix}-heading-${cat.key}`}
                  data-testid={`${idPrefix}-group-${cat.key}`}
                >
                  {/* A separator between sections, never above the first — a
                      rule at the very top of the panel reads as a detached
                      fragment of the trigger rather than as a divider. */}
                  {i > 0 && <div className="my-1 border-t border-gray-200 dark:border-gray-700" />}
                  {/* 🪤 A plain `<div>`, NOT a `<button>` and NOT a heading
                      element: inside `role="menu"` it must be neither focusable
                      nor a `menuitem`. Its id is the group's accessible name. */}
                  <div
                    id={`${idPrefix}-heading-${cat.key}`}
                    className={`px-3 pt-1.5 pb-1 ${MENU_SECTION_HEADING_TEXT}`}
                  >
                    {cat.label}
                  </div>
                  {cat.items.map(renderItem)}
                  {renderFooter(cat.footer)}
                </div>
              )) : (
                <>
                  {visible.map(renderItem)}
                  {/* 🔴 In flat mode a single surviving category's own footer
                      still has to render, or the status override disappears on
                      exactly the rows that have no other category. */}
                  {renderFooter(liveCategories.length === 1 ? liveCategories[0].footer : footer)}
                </>
              )}
            </div>
          );
        }}
      </Dropdown>
    </div>
  );
}
